import { useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getJson, getJsonUnwrapped, http, unwrapPaginatedResponse } from "@/lib/http";
import API_BASE from "@/lib/apiBase";
import { useLocation } from "wouter";
import { Plus, Smartphone, Edit, Trash2, Building2, Upload, Download, FileText, AlertCircle, CheckCircle2, X, Calendar, ArrowLeft, Eye, DollarSign, ChevronDown, ChevronUp, ChevronRight, AlertTriangle, Settings, Lock, Unlock, Loader2, BarChart3, Archive, ArchiveRestore } from "lucide-react";
import { DataTable } from "@/components/tables/data-table";
import { createBookingCycleColumns } from "@/components/tables/booking-cycle-columns";
import { createPackColumns } from "@/components/tables/pack-columns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PackEditorDialog } from "@/components/packs/PackEditorDialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";
import { PackStatusBadge } from "@/components/packs/PackStatusBadge";
import type { Retailer, StoreFormat, Store as StoreType, InsertRetailer, InsertStoreFormat, InsertStore, MediaFormat, InsertMediaFormat, StoreMediaRequirement, InsertStoreMediaRequirement, User, BookingCycle, InsertBookingCycle, Pack, InsertPack, CycleRateTemplate, InsertCycleRateTemplate, RateTemplate, InsertRateTemplate, StoreFormatOperatorAssignment, Task, PackTaskAssignment, FormatGroupItem, AcceptableFormatGroups } from "@shared/schema";
import { usePackStatus } from "@/hooks/usePackStatus";
import { PriorityStoreCheckbox } from "./PriorityStoreCheckbox";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear } from "@/state/yearStore";
import {
  listAssignedStoreMediaFormats,
  listMediaFormatsAvailableForStore,
  listUnassignedStoreMediaFormats,
  type StoreMediaRequirementRow,
} from "@/components/retailer/storeMediaFormatRows";
import { StoreRecordTabs, StoreRecordTwinPanel, type StoreRecordSection } from "@/components/retailer/StoreRecordTabs";
import { useStoreProfileDetail } from "@/features/store-profiles/hooks/useStoreProfilesData";

type StoreMediaDeleteTarget =
  | { step: 'unassign'; assignment: StoreFormatOperatorAssignment }
  | { step: 'remove-format'; requirement: StoreMediaRequirementRow };

export type RetailerWithFormats = Retailer & {
  formats: StoreFormat[];
  storeCount: number;
};

export interface StoresListProps {
  retailerId: number;
}

interface StoreDetailProps {
  store: StoreType;
  onBack: () => void;
  retailerId: number;
  onStoreUpdated: () => void;
  onArchive: () => void;
}

function StoreDetail({ store, onBack, retailerId, onStoreUpdated, onArchive }: StoreDetailProps) {
  const { toast } = useToast();
  const [editingStore, setEditingStore] = useState<StoreType>(store);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(store.notes || "");

  const [pendingDelete, setPendingDelete] = useState<StoreMediaDeleteTarget | null>(null);
  const [isDeleteWarningOpen, setIsDeleteWarningOpen] = useState(false);
  const [recentlyDeleted, setRecentlyDeleted] = useState<any[]>([]);
  const [isAddMediaDialogOpen, setIsAddMediaDialogOpen] = useState(false);
  const [selectedMediaFormatId, setSelectedMediaFormatId] = useState<number>(0);
  const [selectedOperatorId, setSelectedOperatorId] = useState<number>(0);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [section, setSection] = useState<StoreRecordSection>('information');
  useStoreProfileDetail(String(store.id));

  // Sync editingStore when store prop changes (e.g. after refetch)
  useEffect(() => {
    if (!isEditing) {
      setEditingStore(store);
    }
    setNotesValue(store.notes || "");
  }, [store, isEditing]);

  useEffect(() => {
    setSection('information');
  }, [store.id]);

  const { data: storeFormats = [] } = useQuery<StoreFormat[]>({
    queryKey: ["/api/store-formats"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/store-formats');
      const data = await getJson<StoreFormat[]>('/api/store-formats');
      console.log('[QUERY_DEBUG] /api/store-formats response:', data?.length || 0, 'formats');
      return Array.isArray(data) ? data : [];
    },
    select: (allFormats: StoreFormat[]) => allFormats.filter(format => format.retailerId === retailerId),
  });

  const { data: mediaFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ["/api/media-formats", retailerId],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/media-formats for retailer', retailerId);
      const res = await apiFetch(`/api/media-formats?retailerId=${retailerId}`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const data = await res.json();
      console.log('[QUERY_DEBUG] /api/media-formats response:', data?.length || 0, 'formats for retailer', retailerId);
      return data;
    }
  });

  // [PHASE2_PAGINATION] Using getJsonUnwrapped for backward compatibility
  const { data: operators = [] } = useQuery<User[]>({
    queryKey: ["/api/users/operators"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/users/operators');
      const data = await getJsonUnwrapped<User>('/api/users/operators');
      console.log('[QUERY_DEBUG] /api/users/operators response:', data?.length || 0, 'operators');
      return data;
    }
  });

  const { data: retailers = [] } = useQuery<RetailerWithFormats[]>({
    queryKey: ["/api/retailers"],
    queryFn: async () => {
      console.log('[QUERY_DEBUG] Fetching /api/retailers (formats section)');
      const data = await getJson<RetailerWithFormats[]>('/api/retailers');
      console.log('[QUERY_DEBUG] /api/retailers response:', data?.length || 0, 'retailers');
      return Array.isArray(data) ? data : [];
    }
  });



  const { data: storeFormatOperatorAssignments = [] } = useQuery<StoreFormatOperatorAssignment[]>({
    queryKey: ["/api/store-format-operator-assignments", store.id],
    queryFn: async () => {
      const res = await apiFetch(`/api/store-format-operator-assignments?storeIds=${store.id}`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const all = await res.json();
      // Defensive client-side filter because backend emergency route returns all rows
      return Array.isArray(all) ? all.filter((a: any) => a.storeId === store.id) : [];
    },
    enabled: !!store?.id,
  });

  // Fetch store media requirements to get unit counts
  const { data: storeMediaRequirements = [] } = useQuery<StoreMediaRequirement[]>({
    queryKey: ["/api/store-media-requirements"],
    queryFn: async () => {
      const res = await apiFetch('/api/store-media-requirements');
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    select: (allRequirements: StoreMediaRequirement[]) =>
      allRequirements.filter(requirement => requirement.storeId === store.id),
  });

  const assignedMediaFormats = useMemo(
    () => listAssignedStoreMediaFormats(storeFormatOperatorAssignments, storeMediaRequirements),
    [storeFormatOperatorAssignments, storeMediaRequirements],
  );
  const unassignedMediaFormats = useMemo(
    () => listUnassignedStoreMediaFormats(storeMediaRequirements, storeFormatOperatorAssignments),
    [storeMediaRequirements, storeFormatOperatorAssignments],
  );

  // State for POPAd history section
  const [isPopadHistoryExpanded, setIsPopadHistoryExpanded] = useState(false);

  // Fetch POPAd campaign history for this store
  const { data: popadHistoryData, isLoading: popadHistoryLoading } = useQuery<{
    storeId: number;
    storeName: string;
    history: Array<{
      campaignId: number;
      campaignName: string;
      cycleId: number;
      cycleName: string;
      coveragePercent: number;
      plannedUnits: number;
      deliveredUnits: number | null;
      variance: number | null;
      variancePercent: number | null;
      status: string;
      createdAt: string;
    }>;
    summary: {
      totalCampaigns: number;
      totalPlanned: number;
      totalDelivered: number;
      campaignsWithDelivery: number;
      averageVariance: number | null;
    };
  }>({
    queryKey: ["/api/popad/store", store.id, "history"],
    queryFn: async () => {
      const res = await apiFetch(`/api/popad/store/${store.id}/history`);
      if (!res.ok) {
        // If endpoint not found or no data, return empty structure
        if (res.status === 404) {
          return { storeId: store.id, storeName: store.name, history: [], summary: { totalCampaigns: 0, totalPlanned: 0, totalDelivered: 0, campaignsWithDelivery: 0, averageVariance: null } };
        }
        throw new Error(`${res.status}: ${res.statusText}`);
      }
      return res.json();
    },
    enabled: !!store?.id,
    // Don't refetch too frequently
    staleTime: 5 * 60 * 1000,
  });

  const updateStoreMutation = useMutation({
    mutationFn: async (updates: Partial<StoreType>) => {
      const response = await apiRequest(`/api/stores/${store.id}`, "PATCH", updates);
      return await response.json();
    },
    onSuccess: (updatedStore) => {
      setEditingStore(updatedStore);
      setIsEditing(false);
      toast({ title: "Store updated successfully" });
      onStoreUpdated();
    },
    onError: () => {
      toast({ title: "Failed to update store", variant: "destructive" });
    },
  });

  const handleSave = () => {
    const { id, createdAt, updatedAt, retailerId, ...editableFields } = editingStore as any;
    updateStoreMutation.mutate(editableFields);
  };

  // Separate mutation for updating notes only
  const updateNotesMutation = useMutation({
    mutationFn: async (notes: string) => {
      const response = await apiRequest(`/api/stores/${store.id}`, "PATCH", { notes });
      return await response.json();
    },
    onSuccess: (updatedStore) => {
      setEditingStore(updatedStore);
      setNotesValue(updatedStore.notes || "");
      setIsEditingNotes(false);
      toast({ title: "Notes updated successfully" });
      onStoreUpdated();
    },
    onError: () => {
      toast({ title: "Failed to update notes", variant: "destructive" });
    },
  });

  const handleSaveNotes = () => {
    updateNotesMutation.mutate(notesValue);
  };

  // Delete assignment mutation
  const deleteAssignmentMutation = useMutation({
    mutationFn: async (assignmentId: number) => {
      const response = await apiRequest(`/api/store-format-operator-assignments/${assignmentId}`, "DELETE");
      return response;
    },
    onSuccess: async () => {
      if (pendingDelete?.step === 'unassign') {
        const deletedItem = {
          ...pendingDelete.assignment,
          deletedAt: new Date(),
          formatName: mediaFormats.find(f => f.id === pendingDelete.assignment.mediaFormatId)?.name
        };
        setRecentlyDeleted(prev => [deletedItem, ...prev.slice(0, 4)]); // Keep last 5 deletions
      }

      await queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments", store.id], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments"], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements"], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ["/api/stores"], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ["/api/popad/availability"], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: ["/api/store-format-operator-assignments", store.id] });
      await queryClient.refetchQueries({ queryKey: ["/api/store-format-operator-assignments"] });
      await queryClient.refetchQueries({ queryKey: ["/api/store-media-requirements"] });
      await queryClient.refetchQueries({ queryKey: ["/api/stores"] });
      toast({ title: "Operator unassigned. Format moved to Unassigned Media Formats." });
      setIsDeleteWarningOpen(false);
      setPendingDelete(null);
      onStoreUpdated();
    },
    onError: () => {
      toast({ title: "Failed to unassign operator", variant: "destructive" });
    },
  });

  const deleteRequirementMutation = useMutation({
    mutationFn: async (requirementId: number) => {
      return apiRequest(`/api/store-media-requirements/${requirementId}`, "DELETE");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements"], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ["/api/stores"], refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ["/api/popad/availability"], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: ["/api/store-media-requirements"] });
      toast({ title: "Media format removed from this store." });
      setIsDeleteWarningOpen(false);
      setPendingDelete(null);
      onStoreUpdated();
    },
    onError: () => {
      toast({ title: "Failed to remove media format", variant: "destructive" });
    },
  });

  const handleDeleteAssignment = (assignment: StoreFormatOperatorAssignment) => {
    setPendingDelete({ step: 'unassign', assignment });
    setIsDeleteWarningOpen(true);
  };

  const handleDeleteRequirement = (requirement: StoreMediaRequirementRow) => {
    setPendingDelete({ step: 'remove-format', requirement });
    setIsDeleteWarningOpen(true);
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    switch (pendingDelete.step) {
      case 'unassign':
        deleteAssignmentMutation.mutate(pendingDelete.assignment.id);
        return;
      case 'remove-format':
        deleteRequirementMutation.mutate(pendingDelete.requirement.id);
        return;
      default: {
        const _exhaustive: never = pendingDelete;
        return _exhaustive;
      }
    }
  };

  // Restore assignment mutation
  const restoreAssignmentMutation = useMutation({
    mutationFn: async (deletedAssignment: any) => {
      const response = await apiRequest("/api/store-format-operator-assignments", "POST", {
        storeId: deletedAssignment.storeId,
        mediaFormatId: deletedAssignment.mediaFormatId,
        operatorId: deletedAssignment.operatorId
      });
      return await response.json();
    },
    onSuccess: (data, deletedAssignment) => {
      // Invalidate with the correct query key that includes the store ID
      queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments", deletedAssignment.storeId] });
      queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stores"] });
      toast({ title: "Media format restored successfully" });
      onStoreUpdated();
    },
    onError: () => {
      toast({ title: "Failed to restore media format", variant: "destructive" });
    },
  });

  const handleRestore = (deletedAssignment: any) => {
    restoreAssignmentMutation.mutate(deletedAssignment);
    setRecentlyDeleted(prev => prev.filter(item => item.id !== deletedAssignment.id));
  };

  // Add media format assignment mutation
  const addMediaAssignmentMutation = useMutation({
    mutationFn: async (data: { storeId: number; mediaFormatId: number; operatorId: number; quantity: number }) => {
      console.log("🚀 Sending assignment data:", data);
      // First create the store-format-operator assignment
      const assignmentRes = await apiRequest("/api/store-format-operator-assignments", "POST", {
        storeId: data.storeId,
        mediaFormatId: data.mediaFormatId,
        operatorId: data.operatorId
      });
      const assignment = await assignmentRes.json();
      console.log("✅ Assignment created:", assignment);

      // Then create the store media requirement with quantity
      const requirementRes = await apiRequest("/api/store-media-requirements", "POST", {
        storeId: data.storeId,
        mediaFormatId: data.mediaFormatId,
        quantity: data.quantity
      });
      const requirement = await requirementRes.json();
      console.log("✅ Requirement created:", requirement);

      return { assignment, requirement };
    },
    onSuccess: (data) => {
      console.log("✅ Assignment created successfully:", data);
      // Invalidate with the correct query key that includes store.id
      queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments", store.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/store-format-operator-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/media-formats", retailerId, store.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/stores"] });
      toast({ title: "Media format assigned successfully" });
      setIsAddMediaDialogOpen(false);
      setSelectedMediaFormatId(0);
      setSelectedOperatorId(0);
      setSelectedQuantity(1);
      onStoreUpdated();
    },
    onError: (error: Error) => {
      console.error("❌ Add media assignment error:", error);
      console.error("❌ Error details:", {
        message: error.message,
        status: (error as any).status,
        data: (error as any).data
      });
      toast({
        title: "Failed to add media format",
        description: error.message || "Unknown error occurred",
        variant: "destructive"
      });
    },
  });

  // Include restored formats whose operator row was kept after archive.
  // The storeId catalogue excludes every retained operator row, so it cannot be the picker source.
  const storeSpecificMediaFormats = useMemo(
    () => listMediaFormatsAvailableForStore(mediaFormats, storeFormatOperatorAssignments, storeMediaRequirements),
    [mediaFormats, storeFormatOperatorAssignments, storeMediaRequirements],
  );

  const handleAddMediaFormat = () => {
    console.log('🔍 [DEBUG] handleAddMediaFormat called');
    console.log('🔍 [DEBUG] Form state:', {
      selectedMediaFormatId,
      selectedOperatorId,
      selectedQuantity,
      storeId: store.id,
      isPending: addMediaAssignmentMutation.isPending
    });

    if (selectedMediaFormatId && selectedOperatorId && selectedQuantity > 0) {
      console.log('✅ [DEBUG] Validation passed, calling mutation...');
      addMediaAssignmentMutation.mutate({
        storeId: store.id,
        mediaFormatId: selectedMediaFormatId,
        operatorId: selectedOperatorId,
        quantity: selectedQuantity
      });
    } else {
      console.log('❌ [DEBUG] Validation failed');
      toast({ title: "Please select a media format, operator, and valid quantity", variant: "destructive" });
    }
  };



  const displayStore = editingStore;
  const currentFormat = storeFormats.find(f => f.id === displayStore.formatId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Stores
          </Button>
          <div>
            <h2 className="text-2xl font-bold">{store.name}</h2>
            <p className="text-muted-foreground">{store.address}</p>
          </div>
        </div>
        <Button
          onClick={() => {
            if (section !== 'information' && section !== 'media') {
              setSection('information');
              setIsEditing(true);
              return;
            }
            setIsEditing(!isEditing);
          }}
          variant={isEditing ? "outline" : "default"}
        >
          <Edit className="h-4 w-4 mr-2" />
          {isEditing ? "Cancel" : "Edit Store"}
        </Button>
      </div>

      <StoreRecordTabs
        active={section}
        onChange={(next) => {
          if (next !== 'information' && next !== 'media') setIsEditing(false);
          setSection(next);
        }}
      />

      {section === 'information' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Store Information</CardTitle>
            <Button
              variant={store.archivedAt ? "outline" : "secondary"}
              size="sm"
              onClick={onArchive}
            >
              {store.archivedAt
                ? <><ArchiveRestore className="mr-1 h-4 w-4" /> Restore</>
                : <><Archive className="mr-1 h-4 w-4" /> Archive</>}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditing ? (
              <>
                <div>
                  <Label htmlFor="name">Store Name</Label>
                  <Input
                    id="name"
                    value={editingStore.name}
                    onChange={(e) => setEditingStore({ ...editingStore, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={editingStore.address}
                    onChange={(e) => setEditingStore({ ...editingStore, address: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={editingStore.phone || ""}
                    onChange={(e) => setEditingStore({ ...editingStore, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="managerName">Manager Name</Label>
                  <Input
                    id="managerName"
                    value={editingStore.managerName || ""}
                    onChange={(e) => setEditingStore({ ...editingStore, managerName: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="retailerIdentifier">Retailer Identifier (Store Number)</Label>
                  <Input
                    id="retailerIdentifier"
                    value={editingStore.retailerIdentifier || ""}
                    onChange={(e) => setEditingStore({ ...editingStore, retailerIdentifier: e.target.value })}
                    placeholder="e.g., DS-001, Store #123"
                  />
                </div>
                <div>
                  <Label htmlFor="region">Region</Label>
                  <Input
                    id="region"
                    value={editingStore.region || ""}
                    onChange={(e) => setEditingStore({ ...editingStore, region: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="retailer">Retailer</Label>
                  <Select
                    value={editingStore.retailerId?.toString()}
                    onValueChange={(value) => setEditingStore({ ...editingStore, retailerId: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select retailer" />
                    </SelectTrigger>
                    <SelectContent>
                      {retailers.map((retailer) => (
                        <SelectItem key={retailer.id} value={retailer.id.toString()}>
                          {retailer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="format">Store Format</Label>
                  <Select
                    value={editingStore.formatId?.toString()}
                    onValueChange={(value) => setEditingStore({ ...editingStore, formatId: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select format" />
                    </SelectTrigger>
                    <SelectContent>
                      {storeFormats.map((format) => (
                        <SelectItem key={format.id} value={format.id.toString()}>
                          {format.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Operators by Format (read-only) */}
                <div>
                  <Label className="text-sm">Operators by Format (read-only)</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(() => {
                      const rows = storeFormatOperatorAssignments.filter((a: any) => a.storeId === store.id);
                      try { console.log('[ADMIN_STORES_DEBUG][Modal] formatOperators via assignments', { storeId: store.id, rows }); } catch { }
                      if (rows.length === 0) {
                        return <span className="text-sm text-muted-foreground">No format-level operator assignments</span>;
                      }
                      return rows.map((a: any, idx: number) => {
                        const fmt = mediaFormats.find(f => f.id === a.mediaFormatId);
                        const op = operators.find(o => o.id === a.operatorId);
                        return (
                          <Badge key={`${store.id}-${a.mediaFormatId}-${a.operatorId}-${idx}`} variant="outline" className="bg-blue-50 text-blue-800">
                            {(fmt?.name || 'Format') + ' → ' + (op?.name || 'Unassigned')}
                          </Badge>
                        );
                      });
                    })()}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={editingStore.latitude || ""}
                      onChange={(e) => setEditingStore({
                        ...editingStore,
                        latitude: e.target.value ? parseFloat(e.target.value) : null
                      })}
                      placeholder="53.3498"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={editingStore.longitude || ""}
                      onChange={(e) => setEditingStore({
                        ...editingStore,
                        longitude: e.target.value ? parseFloat(e.target.value) : null
                      })}
                      placeholder="-6.2603"
                    />
                  </div>
                </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isPriorityStoreDetail"
                      checked={editingStore.isPriorityStore ?? false}
                      onCheckedChange={(checked) =>
                        setEditingStore({ ...editingStore, isPriorityStore: !!checked })
                      }
                    />
                    <Label htmlFor="isPriorityStoreDetail" className="cursor-pointer">
                      Priority store
                    </Label>
                  </div>
                  <div>
                    <Label htmlFor="hasESL">Electronic Shelf Label (ESL)</Label>
                    <Select
                      value={editingStore.hasESL?.toString()}
                      onValueChange={(value) => setEditingStore({ ...editingStore, hasESL: value === "true" })}
                    >
                    <SelectTrigger>
                      <SelectValue placeholder="Select ESL status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Yes</SelectItem>
                      <SelectItem value="false">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="photo">Store Photo</Label>
                  <Input
                    id="photo"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // For now, just store the file name - in a real app you'd upload to a server
                        setEditingStore({
                          ...editingStore,
                          photoUrl: file.name
                        });
                      }
                    }}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Upload a photo of the store (JPG, PNG, etc.)
                  </p>
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={editingStore.notes || ""}
                    onChange={(e) => setEditingStore({ ...editingStore, notes: e.target.value })}
                    placeholder="Add any additional notes about this store..."
                    rows={3}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Optional notes about the store (operating hours, special instructions, etc.)
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button onClick={handleSave} disabled={updateStoreMutation.isPending}>
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Retailer Identifier (Store Number)</Label>
                    <p>{store.retailerIdentifier || "Not provided"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Phone</Label>
                    <p>{store.phone || "Not provided"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Manager</Label>
                    <p>{store.managerName || "Not assigned"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Region</Label>
                    <p>{store.region || "Not provided"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Store Format</Label>
                    <p>{currentFormat?.name || "No format assigned"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Assigned Operators</Label>
                    <div className="mt-2">
                      {storeFormatOperatorAssignments.length > 0 ? (
                        (() => {
                          // Get unique operators assigned to this store
                          const uniqueOperators = Array.from(new Set(
                            storeFormatOperatorAssignments.map(assignment => assignment.operatorId)
                          )).map(operatorId =>
                            operators.find(op => op.id === operatorId)
                          ).filter(Boolean);

                          return uniqueOperators.map((operator, index) => (
                            <span key={operator?.id || index} className="text-sm">
                              {operator?.name || 'Unknown Operator'}
                              {index < uniqueOperators.length - 1 && ', '}
                            </span>
                          ));
                        })()
                      ) : (
                        <p className="text-sm text-muted-foreground">No operators assigned to media formats</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Latitude</Label>
                    <p>{store.latitude || "Not provided"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Longitude</Label>
                    <p>{store.longitude || "Not provided"}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Priority store</Label>
                    {store.isPriorityStore ? (
                      <span className="text-xs font-semibold tracking-wide text-amber-700">PRIORITY</span>
                    ) : (
                      <p className="text-sm text-muted-foreground">No</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Electronic Shelf Label (ESL)</Label>
                    <Badge variant={store.hasESL ? "default" : "secondary"}>
                      {store.hasESL ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Store Photo</Label>
                    {store.photoUrl ? (
                      <div className="mt-2">
                        <img
                          src={store.photoUrl}
                          alt={`Photo of ${store.name}`}
                          className="w-32 h-24 object-cover rounded border"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                            if (nextElement) {
                              nextElement.style.display = 'block';
                            }
                          }}
                        />
                        <p className="text-sm text-muted-foreground hidden">Photo not available</p>
                      </div>
                    ) : (
                      <p>No photo uploaded</p>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium text-muted-foreground">Notes</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingNotes(!isEditingNotes)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      {isEditingNotes ? "Cancel" : "Edit"}
                    </Button>
                  </div>
                  {isEditingNotes ? (
                    <div className="space-y-2">
                      <Textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        placeholder="Add any additional notes about this store..."
                        rows={3}
                        className="w-full"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSaveNotes}
                          disabled={updateNotesMutation.isPending}
                        >
                          {updateNotesMutation.isPending ? "Saving..." : "Save Notes"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsEditingNotes(false);
                            setNotesValue(editingStore.notes || "");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 bg-muted rounded border">
                      {editingStore.notes ? (
                        <p className="text-sm whitespace-pre-wrap">{editingStore.notes}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">No notes added</p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {section === 'media' && (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Media Formats available in this store</CardTitle>
              {isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    console.log("🔍 Opening Add Media Format modal for store:", store.id);
                    console.log("🔍 Available mediaFormats:", mediaFormats);
                    console.log("🔍 Available operators:", operators);
                    console.log("🔍 Current storeFormatOperatorAssignments:", storeFormatOperatorAssignments);

                    // Filter and log available formats
                    const availableFormats = mediaFormats.filter(format => {
                      return !storeFormatOperatorAssignments.some(assignment =>
                        assignment.mediaFormatId === format.id && assignment.storeId === store.id
                      );
                    });
                    console.log("🔍 Available formats after filtering:", availableFormats);

                    setIsAddMediaDialogOpen(true);
                  }}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Media Format
                </Button>
              )}
            </div>
            {assignedMediaFormats.length === 0 && unassignedMediaFormats.length === 0 && (
              <p className="text-sm text-muted-foreground">No media formats found for this store.</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Assigned Media Formats</Label>
              <div className="space-y-2">
                {assignedMediaFormats.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No assigned media formats.</p>
                ) : assignedMediaFormats.map((assignment) => {
                    const format = mediaFormats.find(f => f.id === assignment.mediaFormatId);
                    const assignedOperator = operators.find(op => op.id === assignment.operatorId);
                    const mediaRequirement = storeMediaRequirements.find(req => req.mediaFormatId === assignment.mediaFormatId && req.storeId === store.id);

                    // Format the volume updated date
                    const volumeUpdatedAt = (mediaRequirement as any)?.updatedAt || (mediaRequirement as any)?.createdAt;
                    const volumeUpdatedDate = volumeUpdatedAt
                      ? new Date(volumeUpdatedAt as string).toLocaleDateString('en-IE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : null;

                    return (
                      <div key={assignment.id} className="flex items-center justify-between p-3 border rounded">
                        <div className="flex-1">
                          <p className="font-medium">{format?.name}</p>
                          {format?.description && (
                            <p className="text-sm text-muted-foreground">{format.description}</p>
                          )}
                          {mediaRequirement && (
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-blue-600">
                                Volume: {mediaRequirement.quantity}
                              </p>
                              {volumeUpdatedDate && (
                                <p className="text-xs text-muted-foreground">
                                  (Updated: {volumeUpdatedDate})
                                </p>
                              )}
                            </div>
                          )}
                          <p className="text-sm text-muted-foreground">
                            Operator: {assignedOperator?.name || 'Unknown operator'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteAssignment(assignment)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
            {unassignedMediaFormats.length > 0 && (
              <div className="space-y-2 border-t pt-4">
                <Label>Unassigned Media Formats</Label>
                <p className="text-xs text-muted-foreground">
                  Still on this shop, but no operator is assigned. Delete removes the format from bookings.
                </p>
                <div className="space-y-2">
                  {unassignedMediaFormats.map((requirement) => {
                    const format = mediaFormats.find(f => f.id === requirement.mediaFormatId);
                    const volumeUpdatedAt = requirement.updatedAt || requirement.createdAt;
                    const volumeUpdatedDate = volumeUpdatedAt
                      ? new Date(volumeUpdatedAt as string).toLocaleDateString('en-IE', { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : null;

                    return (
                      <div key={requirement.id} className="flex items-center justify-between p-3 border border-dashed rounded bg-amber-50/40">
                        <div className="flex-1">
                          <p className="font-medium">{format?.name || `Format ${requirement.mediaFormatId}`}</p>
                          {format?.description && (
                            <p className="text-sm text-muted-foreground">{format.description}</p>
                          )}
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-blue-600">
                              Volume: {requirement.quantity}
                            </p>
                            {volumeUpdatedDate && (
                              <p className="text-xs text-muted-foreground">
                                (Updated: {volumeUpdatedDate})
                              </p>
                            )}
                          </div>
                          <p className="text-sm text-amber-800">No operator assigned</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteRequirement(requirement)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      {/* POPAd Campaign History Section */}
      {(popadHistoryData?.history?.length ?? 0) > 0 && (
        <Card className="mt-6">
          <CardHeader
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setIsPopadHistoryExpanded(!isPopadHistoryExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-lg">POPAd Campaign History</CardTitle>
                <Badge variant="secondary" className="ml-2">
                  {popadHistoryData?.history?.length || 0} campaigns
                </Badge>
              </div>
              <Button variant="ghost" size="sm">
                {isPopadHistoryExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
            {!isPopadHistoryExpanded && popadHistoryData?.summary && (
              <p className="text-sm text-muted-foreground mt-1">
                Total planned: {popadHistoryData.summary.totalPlanned.toLocaleString()} units •
                Delivered: {popadHistoryData.summary.totalDelivered.toLocaleString()} units
              </p>
            )}
          </CardHeader>
          {isPopadHistoryExpanded && (
            <CardContent>
              {popadHistoryLoading ? (
                <div className="text-center py-4 text-muted-foreground">Loading history...</div>
              ) : (
                <>
                  {/* Summary Stats */}
                  {popadHistoryData?.summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-amber-50 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Campaigns</p>
                        <p className="text-lg font-semibold">{popadHistoryData.summary.totalCampaigns}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Planned</p>
                        <p className="text-lg font-semibold">{popadHistoryData.summary.totalPlanned.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Delivered</p>
                        <p className="text-lg font-semibold">{popadHistoryData.summary.totalDelivered.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Avg Variance</p>
                        <p className={`text-lg font-semibold ${popadHistoryData.summary.averageVariance === null
                          ? 'text-muted-foreground'
                          : popadHistoryData.summary.averageVariance >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                          }`}>
                          {popadHistoryData.summary.averageVariance !== null
                            ? `${popadHistoryData.summary.averageVariance > 0 ? '+' : ''}${popadHistoryData.summary.averageVariance}`
                            : '—'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Campaign History Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">Campaign</th>
                          <th className="px-4 py-2 text-left font-medium">Cycle</th>
                          <th className="px-4 py-2 text-right font-medium">Coverage</th>
                          <th className="px-4 py-2 text-right font-medium">Planned</th>
                          <th className="px-4 py-2 text-right font-medium">Delivered</th>
                          <th className="px-4 py-2 text-right font-medium">Variance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {popadHistoryData?.history?.map((item) => (
                          <tr key={`${item.campaignId}-${item.cycleId}`} className="hover:bg-muted/30">
                            <td className="px-4 py-2">
                              <p className="font-medium">{item.campaignName}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(item.createdAt).toLocaleDateString('en-IE')}
                              </p>
                            </td>
                            <td className="px-4 py-2">{item.cycleName}</td>
                            <td className="px-4 py-2 text-right">{item.coveragePercent}%</td>
                            <td className="px-4 py-2 text-right">{item.plannedUnits.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right">
                              {item.deliveredUnits !== null ? item.deliveredUnits.toLocaleString() : '—'}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {item.variance !== null ? (
                                <span className={
                                  item.variance === 0
                                    ? 'text-muted-foreground'
                                    : item.variance > 0
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                }>
                                  {item.variance > 0 ? '+' : ''}{item.variance}
                                  {item.variancePercent !== null && (
                                    <span className="text-xs ml-1">({item.variancePercent > 0 ? '+' : ''}{item.variancePercent}%)</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          )}
        </Card>
      )}
      </>
      )}

      {section !== 'information' && section !== 'media' && (
        <StoreRecordTwinPanel
          storeId={store.id}
          section={section}
          onSectionChange={setSection}
        />
      )}

      {/* Add Media Format Dialog */}
      <Dialog
        open={isAddMediaDialogOpen}
        onOpenChange={(open) => {
          setIsAddMediaDialogOpen(open);
          if (!open) {
            // Reset form when dialog closes
            setSelectedMediaFormatId(0);
            setSelectedOperatorId(0);
            setSelectedQuantity(1);
          }
        }}
        modal={true}
      >
        <DialogContent className={cn("bg-white", "sm:max-w-[425px]")} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Add Media Format to Store</DialogTitle>
            <DialogDescription>
              Select a media format and assign an operator to manage it at this store.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="mediaFormat">Media Format</Label>
              <Select
                value={selectedMediaFormatId.toString()}
                onValueChange={(value) => setSelectedMediaFormatId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select media format" />
                </SelectTrigger>
                <SelectContent className="z-[100] max-h-[200px] overflow-y-auto">
                  {storeSpecificMediaFormats.length === 0 ? (
                    <div className="px-2 py-2 text-sm text-muted-foreground">
                      No unassigned media formats available for this retailer
                    </div>
                  ) : (
                    storeSpecificMediaFormats.map((format) => (
                      <SelectItem key={format.id} value={format.id.toString()}>
                        {format.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="operator">Assigned Operator</Label>
              <Select
                value={selectedOperatorId.toString()}
                onValueChange={(value) => setSelectedOperatorId(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent className="z-[100] max-h-[200px] overflow-y-auto">
                  {operators.length === 0 ? (
                    <div className="px-2 py-2 text-sm text-muted-foreground">
                      No active operators available for this retailer
                    </div>
                  ) : (
                    operators.map((operator) => (
                      <SelectItem key={operator.id} value={operator.id.toString()}>
                        {operator.name || 'Unknown operator'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="quantity">Required Quantity</Label>
              <input
                id="quantity"
                type="number"
                min="1"
                max="999"
                value={selectedQuantity}
                onChange={(e) => setSelectedQuantity(parseInt(e.target.value) || 1)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Enter quantity"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Number of units required for this media format
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddMediaDialogOpen(false)}
              disabled={addMediaAssignmentMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                console.log('🔍 [DEBUG] Button clicked!');
                handleAddMediaFormat();
              }}
              disabled={addMediaAssignmentMutation.isPending || !selectedMediaFormatId || !selectedOperatorId || selectedQuantity <= 0}
            >
              {addMediaAssignmentMutation.isPending ? "Adding..." : "Add Media Format"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning Modal for Media Format Removal */}
      <Dialog open={isDeleteWarningOpen} onOpenChange={(open) => {
        setIsDeleteWarningOpen(open);
        if (!open) setPendingDelete(null);
      }}>
        <DialogContent className={cn("bg-white", "sm:max-w-[425px]")}>
          {pendingDelete?.step === 'unassign' ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Unassign operator
                </DialogTitle>
                <DialogDescription>
                  This takes the operator off the format. The format stays on the shop.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-700 mb-2">
                    Unassigning <strong>{mediaFormats.find(f => f.id === pendingDelete.assignment.mediaFormatId)?.name}</strong> will:
                  </p>
                  <ul className="text-sm text-orange-700 space-y-1 ml-4">
                    <li>• Remove the operator from this format</li>
                    <li>• Move the format to Unassigned Media Formats</li>
                    <li>• Leave the shop capacity in place for campaign bookings</li>
                  </ul>
                </div>
                <p className="text-sm text-muted-foreground">
                  A second delete in Unassigned Media Formats removes the format from the shop.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteWarningOpen(false);
                    setPendingDelete(null);
                  }}
                  disabled={deleteAssignmentMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDelete}
                  disabled={deleteAssignmentMutation.isPending}
                >
                  {deleteAssignmentMutation.isPending ? "Unassigning..." : "Unassign operator"}
                </Button>
              </DialogFooter>
            </>
          ) : pendingDelete?.step === 'remove-format' ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Remove media format
                </DialogTitle>
                <DialogDescription>
                  This removes the format from the shop. It will no longer be bookable.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-700 mb-2">
                    Removing <strong>{mediaFormats.find(f => f.id === pendingDelete.requirement.mediaFormatId)?.name}</strong> from this store will:
                  </p>
                  <ul className="text-sm text-orange-700 space-y-1 ml-4">
                    <li>• Delete the leftover shop capacity</li>
                    <li>• Stop this format appearing in future campaign bookings</li>
                  </ul>
                </div>
                <p className="text-sm text-muted-foreground">
                  This cannot be undone. You will need to add the format again if you want it back.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsDeleteWarningOpen(false);
                    setPendingDelete(null);
                  }}
                  disabled={deleteRequirementMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDelete}
                  disabled={deleteRequirementMutation.isPending}
                >
                  {deleteRequirementMutation.isPending ? "Removing..." : "Remove format"}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function StoresList({ retailerId }: StoresListProps) {
  const { toast } = useToast();
  const [editingStore, setEditingStore] = useState<StoreType | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isMediaDialogOpen, setIsMediaDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreType | null>(null);
  const [viewingStore, setViewingStore] = useState<StoreType | null>(null);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [storeToArchive, setStoreToArchive] = useState<StoreType | null>(null);
  const [showArchivedStores, setShowArchivedStores] = useState(false);
  const [isBulkUploadDialogOpen, setIsBulkUploadDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [storeFormData, setStoreFormData] = useState<Partial<InsertStore>>({
    name: "",
    address: "",
    formatId: undefined,
    region: "",
    phone: "",
    retailerId: retailerId,
    managerName: "",
    operatorId: null,
    retailerIdentifier: "",
    notes: "",
    isPriorityStore: false,
  });

  // Use original working stores query (Phase 3 pagination deferred)
  // [PHASE2_PAGINATION] Using unwrapPaginatedResponse for backward compatibility
  const { data: stores = [], isLoading } = useQuery<StoreType[]>({
    queryKey: ["/api/stores", retailerId, showArchivedStores],
    queryFn: async () => {
      const url = showArchivedStores
        ? `/api/stores?retailerId=${retailerId}&pageSize=200&includeArchived=true`
        : `/api/stores?retailerId=${retailerId}&pageSize=200`;
      const res = await apiFetch(url);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      const json = await res.json();
      const data = unwrapPaginatedResponse<StoreType>(json);
      return data.sort((a: StoreType, b: StoreType) => a.name.trim().toLowerCase().localeCompare(b.name.trim().toLowerCase()));
    },
  });

  const { data: storeFormats = [] } = useQuery<StoreFormat[]>({
    queryKey: ["/api/store-formats", retailerId],
    queryFn: async () => {
      const res = await apiFetch(`/api/store-formats?retailerId=${retailerId}`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
  });

  const { data: mediaFormats = [] } = useQuery<MediaFormat[]>({
    queryKey: ["/api/media-formats", retailerId],
    queryFn: async () => {


      const res = await http(`${API_BASE}/api/media-formats?retailerId=${retailerId}`, {
        component: 'RetailerManagement-MediaFormats'
      });

      const data = await res.json();

      // Update API status on success
      if ((window as any).updateApiStatus) {
        (window as any).updateApiStatus('retailers');
      }

      return data;
    },
    enabled: !!retailerId,
  });

  // [PHASE2_PAGINATION] Using unwrapPaginatedResponse for backward compatibility
  const { data: operators = [] } = useQuery<User[]>({
    queryKey: ["/api/users/operators", retailerId],
    queryFn: async () => {
      const res = await http(`${API_BASE}/api/users/operators?retailerId=${retailerId}&activeOnly=true&pageSize=200`, {
        component: 'RetailerManagement-Operators'
      });

      const json = await res.json();
      const data = unwrapPaginatedResponse<User>(json);

      // Update API status on success
      if ((window as any).updateApiStatus) {
        (window as any).updateApiStatus('storeOperatorSummary');
      }

      return data;
    },
  });

  const { data: retailers = [] } = useQuery<RetailerWithFormats[]>({
    queryKey: ["/api/retailers"],
    queryFn: async () => {
      const res = await apiFetch('/api/retailers');
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
  });

  const { data: storeFormatOperatorAssignments = [] } = useQuery<any[]>({
    queryKey: ["/api/store-format-operator-assignments"],
    queryFn: async () => {
      const res = await apiFetch('/api/store-format-operator-assignments');
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
  });

  const { data: storeMediaRequirements = [] } = useQuery<any[]>({
    queryKey: ["/api/store-media-requirements"],
    queryFn: async () => {
      const res = await apiFetch('/api/store-media-requirements');
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
  });

  const createStoreMutation = useMutation({
    mutationFn: async (data: InsertStore) => {
      return await apiRequest("/api/stores", "POST", data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/stores"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["/api/retailers"], exact: false });
      toast({
        title: "Success",
        description: "Store created successfully",
      });
      setIsCreateDialogOpen(false);
      setStoreFormData({
        name: "",
        address: "",
        formatId: 0,
        region: "",
        phone: "",
        retailerId: retailerId,
        managerName: "",
        operatorId: null,
        retailerIdentifier: "",
        notes: "",
        isPriorityStore: false,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: "Failed to create store",
        variant: "destructive",
      });
    },
  });

  const updateStoreMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<StoreType> }) => {
      return await apiRequest(`/api/stores/${data.id}`, "PATCH", data.updates);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/stores"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["/api/retailers"], exact: false });
      toast({
        title: "Success",
        description: "Store updated successfully",
      });
      setIsEditDialogOpen(false);
      setEditingStore(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const archiveStoreMutation = useMutation({
    mutationFn: async ({ id, archive }: { id: number; archive: boolean }) => {
      return apiRequest(`/api/stores/${id}/archive`, "PATCH", { archive });
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["/api/stores"], exact: false, refetchType: 'active' });
      await queryClient.invalidateQueries({ queryKey: ["/api/retailers"], exact: false, refetchType: 'active' });
      setIsArchiveDialogOpen(false);
      setStoreToArchive(null);
      setViewingStore((current) => {
        if (!current || current.id !== variables.id) return current;
        return {
          ...current,
          archivedAt: variables.archive ? new Date() : null,
        };
      });
      toast({
        title: "Success",
        description: variables.archive
          ? "Store archived successfully"
          : "Store restored successfully",
      });
    },
    onError: (error: any) => {
      const message = error?.data?.error || error?.message || "Failed to archive store";
      toast({ title: "Error", description: message, variant: "destructive" });
    },
  });

  const bulkUploadMutation = useMutation({
    mutationFn: async (data: { file: File; retailerId: number }) => {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("retailerId", data.retailerId.toString());

      const response = await fetch("/api/stores/bulk-upload", {
        method: "POST",
        body: formData,
        credentials: 'include',
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const serverError = responseData?.error || responseData?.message || responseData?.detail;
        throw new Error(serverError || `Upload failed (${response.status})`);
      }

      return responseData;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["/api/stores"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["/api/retailers"], exact: false });
      setUploadResult(data);
      setCsvFile(null);
      toast({
        title: "Success",
        description: `Successfully uploaded ${data.processedStores || 0} stores`,
      });
    },
    onError: (error: any) => {
      setUploadResult({ success: false, message: error.message });
      toast({
        title: "Error",
        description: error.message || "Failed to upload stores",
        variant: "destructive",
      });
    },
  });

  const handleCreateStore = () => {
    if (!storeFormData.name || !storeFormData.formatId || !storeFormData.address) {
      toast({
        title: "Error",
        description: "Store name, format, and address are required",
        variant: "destructive",
      });
      return;
    }
    createStoreMutation.mutate(storeFormData as InsertStore);
  };

  const handleEditStore = (store: StoreType) => {
    setEditingStore(store);
    setStoreFormData({
      name: store.name,
      address: store.address,
      formatId: store.formatId,
      region: store.region || "",
      phone: store.phone || "",
      managerName: store.managerName || "",
      operatorId: store.operatorId || null,
      notes: store.notes || "",
      retailerIdentifier: store.retailerIdentifier || "",
      isPriorityStore: store.isPriorityStore ?? false,
    });


    setIsEditDialogOpen(true);
  };

  const handleManageMedia = (store: StoreType) => {
    setSelectedStore(store);
    setIsMediaDialogOpen(true);
  };

  const handleUpdateStore = async () => {
    if (!editingStore) return;

    try {
      // Update store basic information
      await updateStoreMutation.mutateAsync({
        id: editingStore.id,
        updates: {
          name: storeFormData.name,
          address: storeFormData.address,
          formatId: storeFormData.formatId,
          region: storeFormData.region || null,
          phone: storeFormData.phone || null,
          managerName: storeFormData.managerName || null,
          operatorId: storeFormData.operatorId || null,
          retailerIdentifier: storeFormData.retailerIdentifier || null,
          notes: storeFormData.notes || null,
          isPriorityStore: storeFormData.isPriorityStore ?? false,
        },
      });



      toast({
        title: "Success",
        description: "Store updated successfully"
      });

      setIsEditDialogOpen(false);
      setEditingStore(null);

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update store",
        variant: "destructive"
      });
    }
  };

  const handleArchiveStore = (store: StoreType) => {
    setStoreToArchive(store);
    setIsArchiveDialogOpen(true);
  };

  const confirmArchiveStore = () => {
    if (storeToArchive) {
      archiveStoreMutation.mutate({
        id: storeToArchive.id,
        archive: !storeToArchive.archivedAt,
      });
    }
  };

  const downloadTemplate = () => {
    const retailer = retailers.find((r: RetailerWithFormats) => r.id === retailerId);
    if (!retailer) return;

    const csvContent = [
      "name,address,phone,managerName,retailerIdentifier,latitude,longitude,region,retailer_name,format_name",
      `${retailer.name} Store 1,123 Main Street,+353-1-555-0001,John Manager,DS-001,53.3498,-6.2603,Dublin,${retailer.name},${storeFormats[0]?.name || 'Convenience Store'}`,
      `${retailer.name} Store 2,456 High Street,+353-1-555-0002,Jane Manager,DS-002,53.3415,-6.2553,Dublin,${retailer.name},${storeFormats[0]?.name || 'Convenience Store'}`
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${retailer.name.toLowerCase().replace(/\s+/g, '_')}_stores_template.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/csv") {
      setCsvFile(file);
      setUploadResult(null);
    } else {
      toast({
        title: "Invalid File",
        description: "Please select a valid CSV file",
        variant: "destructive",
      });
    }
  };

  const handleBulkUpload = () => {
    if (!csvFile) {
      toast({
        title: "No File Selected",
        description: "Please select a CSV file to upload",
        variant: "destructive",
      });
      return;
    }
    bulkUploadMutation.mutate({ file: csvFile, retailerId });
  };

  const resetStoreForm = () => {
    setStoreFormData({
      name: "",
      address: "",
      formatId: storeFormats[0]?.id,
      region: "",
      phone: "",
      retailerId,
      managerName: "",
      operatorId: null,
      retailerIdentifier: "",
      notes: "",
      isPriorityStore: false,
    });
  };

  const openCreateStoreDialog = () => {
    resetStoreForm();
    setIsCreateDialogOpen(true);
  };

  const getFormatName = (formatId: number | null | undefined) => {
    if (!formatId) return 'Unknown format';
    const match = storeFormats.find((format) => format.id === formatId);
    return match?.name ?? 'Unknown format';
  };

  const getOperatorName = (operatorId: number | null | undefined) => {
    if (!operatorId) return 'Unassigned';
    const match = operators.find((operator) => operator.id === operatorId);
    return match?.name || match?.username || match?.email || 'Unassigned';
  };

  // Show store detail view if a store is selected
  const archiveDialog = (
    <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
      <AlertDialogContent className={cn("bg-white")}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {storeToArchive?.archivedAt ? "Restore Store?" : "Archive Store?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {storeToArchive?.archivedAt
              ? `This will restore "${storeToArchive?.name}" and make it available for campaigns and packs again.`
              : `This will archive "${storeToArchive?.name}". It will be hidden from campaign creation, pack creation, and operator views, but can be restored later.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmArchiveStore}
            disabled={archiveStoreMutation.isPending}
          >
            {archiveStoreMutation.isPending
              ? (storeToArchive?.archivedAt ? "Restoring..." : "Archiving...")
              : (storeToArchive?.archivedAt ? "Restore" : "Archive")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (viewingStore) {
    // Get the latest store data from the stores query
    const currentStore = stores.find(s => s.id === viewingStore.id) || viewingStore;
    return (
      <>
        <StoreDetail
          store={currentStore}
          onBack={() => setViewingStore(null)}
          retailerId={retailerId}
          onArchive={() => handleArchiveStore(currentStore)}
          onStoreUpdated={() => {
            // Refresh the stores data to get updated store information
            queryClient.invalidateQueries({ queryKey: ["/api/stores"] });
          }}
        />
        {archiveDialog}
      </>
    );
  }

  if (isLoading) {
    return <div className="text-center py-4">Loading stores...</div>;
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">
          {stores.length} store{stores.length !== 1 ? 's' : ''} configured
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setIsBulkUploadDialogOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Bulk Upload CSV
          </Button>
          <Button size="sm" onClick={openCreateStoreDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Add Store
          </Button>
        </div>
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Checkbox
          id="show-archived-stores"
          checked={showArchivedStores}
          onCheckedChange={(checked) => setShowArchivedStores(!!checked)}
        />
        <label
          htmlFor="show-archived-stores"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          Show Archived Stores
        </label>
      </div>

      {storeFormats.length === 0 ? (
        <Alert className="mb-4 border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Store Formats Required:</strong> You must create at least one Store Format before adding stores. Go to the "Store Formats" tab to create one.
          </AlertDescription>
        </Alert>
      ) : stores.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <p>No stores configured yet.</p>
          <p className="mt-2">Use Add Store or Bulk Upload to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {stores.map((store) => {
            const assignments = storeFormatOperatorAssignments.filter((assignment: any) => assignment.storeId === store.id);
            const requirements = storeMediaRequirements.filter((requirement: any) => requirement.storeId === store.id);
            const formatName = getFormatName(store.formatId);
            // Operator label is derived exclusively from per-format operator
            // assignments in the "Format and Operator Assignment" tab. The
            // store-level operatorId is intentionally ignored.
            const uniqueOperatorIds = Array.from(
              new Set(
                assignments
                  .map((a: any) => a.operatorId)
                  .filter((id: any): id is number => typeof id === 'number')
              )
            );
            let operatorBadgeLabel: string;
            if (uniqueOperatorIds.length === 0) {
              operatorBadgeLabel = 'Operator Unassigned';
            } else if (uniqueOperatorIds.length === 1) {
              operatorBadgeLabel = getOperatorName(uniqueOperatorIds[0]);
            } else {
              operatorBadgeLabel = `${uniqueOperatorIds.length} operators`;
            }
            return (
              <Card key={store.id} className={store.archivedAt ? "opacity-60" : ""}>
                <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">{store.name}</h3>
                      {store.retailerIdentifier && (
                        <Badge variant="outline" className="text-xs font-mono">{store.retailerIdentifier}</Badge>
                      )}
                      {store.archivedAt && (
                        <Badge variant="destructive" className="bg-amber-100 text-amber-800 border-amber-300">
                          Archived
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{store.address}</p>
                    <div className="flex flex-wrap gap-2 pt-2 text-xs sm:text-sm">
                      <Badge variant="secondary">{formatName}</Badge>
                      <Badge variant="secondary">{operatorBadgeLabel}</Badge>
                      <Badge variant="secondary">{assignments.length} assignment{assignments.length === 1 ? '' : 's'}</Badge>
                      <Badge variant="secondary">{requirements.length} media requirement{requirements.length === 1 ? '' : 's'}</Badge>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <PriorityStoreCheckbox
                      storeId={store.id}
                      storeName={store.name}
                      checked={store.isPriorityStore ?? false}
                    />
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => setViewingStore(store)}>
                      <Eye className="mr-1 h-4 w-4" />
                      More Details
                    </Button>
                  </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          setIsCreateDialogOpen(open);
          if (!open) {
            resetStoreForm();
          }
        }}
      >
        <DialogContent className={cn("bg-white", "max-w-2xl")}>
          <DialogHeader>
            <DialogTitle>Add Store</DialogTitle>
            <DialogDescription>Configure the store details for this retailer.</DialogDescription>
          </DialogHeader>
          {storeFormats.length === 0 && (
            <Alert className="mb-4 border-amber-200 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                You need to create at least one <strong>Store Format</strong> before adding stores. Go to the "Store Formats" tab to create one.
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="storeName">Store Name</Label>
                <Input
                  id="storeName"
                  value={storeFormData.name ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, name: event.target.value })}
                  placeholder="e.g., Main Street SuperValu"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storeFormat">Store Format</Label>
                <Select
                  value={storeFormData.formatId?.toString() ?? ""}
                  onValueChange={(value) => setStoreFormData({ ...storeFormData, formatId: value ? Number(value) : undefined })}
                >
                  <SelectTrigger id="storeFormat" disabled={storeFormats.length === 0}>
                    <SelectValue placeholder={storeFormats.length ? "Select store format" : "No formats available"} />
                  </SelectTrigger>
                  <SelectContent>
                    {storeFormats.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        Create store formats first
                      </div>
                    ) : (
                      storeFormats.map((format) => (
                        <SelectItem key={format.id} value={format.id.toString()}>
                          {format.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="storeAddress">Address</Label>
              <Textarea
                id="storeAddress"
                value={storeFormData.address ?? ""}
                onChange={(event) => setStoreFormData({ ...storeFormData, address: event.target.value })}
                placeholder="Street, City, County"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="storeRegion">Region</Label>
                <Input
                  id="storeRegion"
                  value={storeFormData.region ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, region: event.target.value })}
                  placeholder="Region or territory"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storePhone">Phone</Label>
                <Input
                  id="storePhone"
                  value={storeFormData.phone ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, phone: event.target.value })}
                  placeholder="+353 ..."
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="storeManager">Manager</Label>
                <Input
                  id="storeManager"
                  value={storeFormData.managerName ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, managerName: event.target.value })}
                  placeholder="Store manager name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storeIdentifier">Retailer Identifier</Label>
                <Input
                  id="storeIdentifier"
                  value={storeFormData.retailerIdentifier ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, retailerIdentifier: event.target.value })}
                  placeholder="Retailer store ID"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="storeOperator">Operator</Label>
              <Select
                value={storeFormData.operatorId?.toString() ?? "none"}
                onValueChange={(value) =>
                  setStoreFormData({ ...storeFormData, operatorId: value === "none" ? null : Number(value) })
                }
              >
                <SelectTrigger id="storeOperator">
                  <SelectValue placeholder="Assign operator" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {operators.map((operator) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator.name || operator.username || operator.email || `Operator ${operator.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="storeNotes">Notes</Label>
              <Textarea
                id="storeNotes"
                value={storeFormData.notes ?? ""}
                onChange={(event) => setStoreFormData({ ...storeFormData, notes: event.target.value })}
                placeholder="Internal notes for operators"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="createIsPriorityStore"
                checked={storeFormData.isPriorityStore ?? false}
                onCheckedChange={(checked) =>
                  setStoreFormData({ ...storeFormData, isPriorityStore: !!checked })
                }
              />
              <Label htmlFor="createIsPriorityStore" className="cursor-pointer">
                Priority store
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                resetStoreForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateStore} disabled={createStoreMutation.isPending}>
              {createStoreMutation.isPending ? "Creating..." : "Create Store"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingStore(null);
            resetStoreForm();
          }
        }}
      >
        <DialogContent className={cn("bg-white", "max-w-2xl")}>
          <DialogHeader>
            <DialogTitle>Edit Store</DialogTitle>
            <DialogDescription>Update store details and assignments.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="editStoreName">Store Name</Label>
                <Input
                  id="editStoreName"
                  value={storeFormData.name ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, name: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editStoreFormat">Store Format</Label>
                <Select
                  value={storeFormData.formatId?.toString() ?? ""}
                  onValueChange={(value) => setStoreFormData({ ...storeFormData, formatId: value ? Number(value) : undefined })}
                >
                  <SelectTrigger id="editStoreFormat" disabled={storeFormats.length === 0}>
                    <SelectValue placeholder={storeFormats.length ? "Select store format" : "No formats available"} />
                  </SelectTrigger>
                  <SelectContent>
                    {storeFormats.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground">
                        Create store formats first
                      </div>
                    ) : (
                      storeFormats.map((format) => (
                        <SelectItem key={format.id} value={format.id.toString()}>
                          {format.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editStoreAddress">Address</Label>
              <Textarea
                id="editStoreAddress"
                value={storeFormData.address ?? ""}
                onChange={(event) => setStoreFormData({ ...storeFormData, address: event.target.value })}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="editStoreRegion">Region</Label>
                <Input
                  id="editStoreRegion"
                  value={storeFormData.region ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, region: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editStorePhone">Phone</Label>
                <Input
                  id="editStorePhone"
                  value={storeFormData.phone ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, phone: event.target.value })}
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="editStoreManager">Manager</Label>
                <Input
                  id="editStoreManager"
                  value={storeFormData.managerName ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, managerName: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editStoreIdentifier">Retailer Identifier</Label>
                <Input
                  id="editStoreIdentifier"
                  value={storeFormData.retailerIdentifier ?? ""}
                  onChange={(event) => setStoreFormData({ ...storeFormData, retailerIdentifier: event.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editStoreOperator">Operator</Label>
              <Select
                value={storeFormData.operatorId?.toString() ?? "none"}
                onValueChange={(value) =>
                  setStoreFormData({ ...storeFormData, operatorId: value === "none" ? null : Number(value) })
                }
              >
                <SelectTrigger id="editStoreOperator">
                  <SelectValue placeholder="Assign operator" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {operators.map((operator) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator.name || operator.username || operator.email || `Operator ${operator.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editStoreNotes">Notes</Label>
              <Textarea
                id="editStoreNotes"
                value={storeFormData.notes ?? ""}
                onChange={(event) => setStoreFormData({ ...storeFormData, notes: event.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="editIsPriorityStore"
                checked={storeFormData.isPriorityStore ?? false}
                onCheckedChange={(checked) =>
                  setStoreFormData({ ...storeFormData, isPriorityStore: !!checked })
                }
              />
              <Label htmlFor="editIsPriorityStore" className="cursor-pointer">
                Priority store
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                setEditingStore(null);
                resetStoreForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateStore} disabled={updateStoreMutation.isPending}>
              {updateStoreMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isBulkUploadDialogOpen}
        onOpenChange={(open) => {
          setIsBulkUploadDialogOpen(open);
          if (!open) {
            setCsvFile(null);
            setUploadResult(null);
          }
        }}
      >
        <DialogContent className={cn("bg-white", "max-w-2xl")}>
          <DialogHeader>
            <DialogTitle>Bulk Upload Stores</DialogTitle>
            <DialogDescription>Upload a CSV or Excel file to create stores for this retailer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* File input */}
            <Input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChange}
            />

            {/* Instructions */}
            <div className="border rounded-lg p-4 bg-slate-50 space-y-3">
              <div>
                <p className="text-sm font-medium text-green-700 mb-1">✓ Required columns:</p>
                <div className="flex gap-2">
                  <code className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-mono">name</code>
                  <code className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-mono">address</code>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">○ Optional columns:</p>
                <div className="flex flex-wrap gap-1">
                  <code className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-mono">retailerIdentifier</code>
                  <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">phone</code>
                  <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">managerName</code>
                  <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">latitude</code>
                  <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">longitude</code>
                  <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">region</code>
                  <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">notes</code>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  <span className="font-medium">retailerIdentifier</span> = Store Number (e.g., DS-001, Store #123)
                </p>
              </div>
            </div>

            {/* Result display */}
            {uploadResult && (
              <Alert variant={uploadResult.success ? "default" : "destructive"}>
                <AlertDescription>
                  {uploadResult.message || uploadResult.error || (uploadResult.success
                    ? `Successfully created ${uploadResult.processedStores ?? 0} stores.`
                    : 'Upload failed. Please check your file format.')}
                </AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsBulkUploadDialogOpen(false);
                setCsvFile(null);
                setUploadResult(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkUpload} disabled={bulkUploadMutation.isPending || !csvFile}>
              {bulkUploadMutation.isPending ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {archiveDialog}
    </>
  );
}
